import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  useTheme,
  Box,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { CustomTextField, SectionTitle } from "~components/atoms";
import { useStreamContext } from "~contexts/Stream";
import { useSocketContext } from "~contexts/Socket";
import { useToastNotificationContext } from "~contexts/ToastNotification";
import { authFetch } from "~helpers/authFetch";

const Tapumes: React.FC = () => {
  const theme: any = useTheme();
  const { currentStream, stream } = useStreamContext();
  const { updateStreamSocket } = useSocketContext();
  const { setToastData } = useToastNotificationContext();
  const [desktopImage, setDesktopImage] = useState("");
  const [mobileImage, setMobileImage] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [placeholderImage, setPlaceholderImage] = useState("");
  const [placeholderImageWithSeller, setPlaceholderImageWithSeller] = useState("");
  const [hasCountdown, setHasCountdown] = useState(false);
  const [countdownDate, setCountdownDate] = useState("");
  const [countdownTime, setCountdownTime] = useState("");
  const [, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Carregar dados quando a stream mudar
  const loadTapumesData = useCallback(async () => {
    if (!currentStream?.id) return;

    setLoading(true);
    try {
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/tapumes/${currentStream.id}`,
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        setDesktopImage(data.tapume?.desktopImage || "");
        setMobileImage(data.tapume?.mobileImage || "");
        setButtonText(data.tapume?.buttonText || "");
        setPlaceholderImage(data.placeholder?.image || "");
        setPlaceholderImageWithSeller(data.placeholder?.imageWithSeller || "");

        // Configurar countdown se existir
        if (data.tapume?.countdown) {
          setHasCountdown(true);
          setCountdownDate(data.tapume.countdown.date || "");
          setCountdownTime(data.tapume.countdown.time || "");
        } else {
          setHasCountdown(false);
          setCountdownDate("");
          setCountdownTime("");
        }
      }
    } catch (error) {
      console.error("Error loading tapumes data:", error);
    } finally {
      setLoading(false);
    }
  }, [currentStream?.id]);

  useEffect(() => {
    if (currentStream?.id) {
      loadTapumesData();
    }
  }, [currentStream?.id, loadTapumesData]);

  const handlePublish = async () => {
    if (!currentStream?.id) {
      setToastData(
        {
          title: "Erro",
          text: "Nenhuma stream selecionada",
        },
        "error"
      );
      return;
    }

    try {
      setSaving(true);

      // Preparar dados do countdown
      const countdownData =
        hasCountdown && countdownDate && countdownTime
          ? {
              date: countdownDate,
              time: countdownTime,
            }
          : null;

      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/tapumes/${currentStream.id}`,
        method: "PATCH",
        data: {
          tapume: {
            desktopImage,
            mobileImage,
            buttonText,
            countdown: countdownData,
          },
          placeholder: {
            image: placeholderImage,
            imageWithSeller: placeholderImageWithSeller,
          },
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || "Erro ao salvar tapumes");
        (error as any).status = response.status;
        throw error;
      }

      setToastData(
        {
          title: "Sucesso",
          text: "Tapumes salvos com sucesso",
        },
        "success"
      );

      // Notifica frontend para recarregar apenas o tema em tempo real.
      updateStreamSocket({
        is_live: stream?.is_live ?? 0,
        has_chat: stream?.has_chat ?? 0,
        has_reaction: stream?.has_reaction ?? 0,
        has_measure: stream?.has_measure ?? 0,
        themeUpdated: true,
      });
    } catch (error: any) {
      console.error("Error saving tapumes:", error);
      let errorMessage = "Erro ao salvar tapumes";

      if (error.status === 400) {
        errorMessage = error.message || "Dados inválidos";
      }

      setToastData(
        {
          title: "Erro",
          text: errorMessage,
        },
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!currentStream) {
    return (
      <Box>
        <Typography
          variant="h4"
          sx={{ color: theme.palette.grey[0], mb: 0.5, fontSize: "30px !important" }}
        >
          tapumes
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#858585", mb: "40px", fontSize: "15px" }}
        >
          Selecione uma marca para editar os tapumes
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ color: theme.palette.grey[0], mb: 0.5, fontSize: "30px !important" }}
          >
            tapumes
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#858585", mb: "40px", fontSize: "15px" }}
          >
            não esqueça de publicar as informações depois de salvas
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handlePublish}
          disabled={saving}
          sx={{
            backgroundColor: "#FFFFFF",
            color: "#000000",
            borderRadius: "100px",
            paddingTop: "16px",
            paddingRight: "24px",
            paddingBottom: "16px",
            paddingLeft: "24px",
            height: "48px",
            gap: "12px",
            fontWeight: 400,
            fontSize: "11px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F5F5F5",
            },
            "&:disabled": {
              backgroundColor: "#666666",
              color: "#FFFFFF",
            },
          }}
        >
          {saving ? (
            <>
              <CircularProgress size={16} sx={{ color: "#000000" }} />
              salvando...
            </>
          ) : (
            <>
              publicar
              <Box
                component="img"
                src="/icons/publish.svg"
                alt="publicar"
                sx={{
                  width: "24px",
                  height: "24px",
                }}
              />
            </>
          )}
        </Button>
      </Box>
      <Box sx={{ maxWidth: "720px" }}>
        {/* Bloco 1: Tapume */}
        <Box>
          <SectionTitle sx={{ mb: 2 }}>tapume</SectionTitle>

          {/* Inputs Desktop e Mobile lado a lado */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: theme.palette.grey[0],
                  fontSize: "15px",
                  fontWeight: 400,
                  mb: 1.5,
                }}
              >
                desktop
              </Typography>
              {/* Preview da imagem desktop */}
              <Box
                sx={{
                  mb: 1.5,
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: theme.palette.grey[700],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                }}
              >
                {desktopImage && (
                  <Box
                    component="img"
                    src={desktopImage}
                    alt="Preview desktop"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Box>
              <CustomTextField
                value={desktopImage}
                onChange={(e) => setDesktopImage(e.target.value)}
                label="link da imagem"
                fullWidth
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: theme.palette.grey[0],
                  fontSize: "15px",
                  fontWeight: 400,
                  mb: 1.5,
                }}
              >
                mobile
              </Typography>
              {/* Preview da imagem mobile */}
              <Box
                sx={{
                  mb: 1.5,
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: theme.palette.grey[700],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                }}
              >
                {mobileImage && (
                  <Box
                    component="img"
                    src={mobileImage}
                    alt="Preview mobile"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Box>
              <CustomTextField
                value={mobileImage}
                onChange={(e) => setMobileImage(e.target.value)}
                label="link da imagem"
                fullWidth
              />
            </Box>
          </Box>

          {/* Input texto do botão com contador */}
          <Box sx={{ mb: 2, position: "relative" }}>
            <CustomTextField
              value={buttonText}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 20) {
                  setButtonText(value);
                }
              }}
              label="texto do botão"
              fullWidth
              inputProps={{ maxLength: 20 }}
            />
            <Typography
              sx={{
                position: "absolute",
                bottom: "8px",
                right: "16px",
                fontSize: "10px",
                color: "#FFFFFF",
                pointerEvents: "none",
              }}
            >
              {buttonText.length}/20
            </Typography>
          </Box>

          {/* Botão adicionar countdown ou bloco de countdown */}
          {!hasCountdown ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                mt: 7, // 56px
              }}
            >
              <Button
                variant="text"
                onClick={() => setHasCountdown(true)}
                sx={{
                  color: theme.palette.grey[400],
                  fontSize: "15px",
                  fontWeight: 400,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: theme.palette.grey[0],
                  },
                }}
              >
                + adicionar countdown
              </Button>
            </Box>
          ) : (
            <Box sx={{ mt: 7 }}>
              {" "}
              {/* 56px */}
              {/* Título e ícone de lixeira */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <SectionTitle>countdown</SectionTitle>
                <IconButton
                  onClick={() => {
                    setHasCountdown(false);
                    setCountdownDate("");
                    setCountdownTime("");
                  }}
                  sx={{
                    color: theme.palette.grey[400],
                    padding: "4px",
                    width: "fit-content",
                    "&:hover": {
                      color: theme.palette.error.main,
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/icons/delete.png"
                    alt="excluir"
                    sx={{
                      width: "24px",
                      height: "24px",
                    }}
                  />
                </IconButton>
              </Box>
              {/* Inputs de data e horário lado a lado */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 2,
                }}
              >
                <CustomTextField
                  type="date"
                  value={countdownDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Garantir que o ano tenha 4 dígitos
                    if (value) {
                      const parts = value.split("-");
                      if (parts[0] && parts[0].length === 4) {
                        setCountdownDate(value);
                      }
                    } else {
                      setCountdownDate(value);
                    }
                  }}
                  label="insira a data"
                  fullWidth
                  inputProps={{
                    max: "9999-12-31",
                    min: "1000-01-01",
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                      color: "#FFFFFF",
                    },
                    "& input[type='date']::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                      cursor: "pointer",
                    },
                  }}
                />
                <CustomTextField
                  type="time"
                  value={countdownTime}
                  onChange={(e) => setCountdownTime(e.target.value)}
                  label="insira o horário"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                      color: "#FFFFFF",
                    },
                    "& input[type='time']::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                      cursor: "pointer",
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* Bloco 2: Selo placeholder mobile */}
        <Box sx={{ mt: 7, mb: 4 }}>
          {" "}
          {/* 56px entre countdown e selos */}
          <SectionTitle sx={{ mb: 0 }}>selo placeholder mobile</SectionTitle>
          <Typography
            sx={{
              color: "#858585",
              fontSize: "11px",
              fontWeight: 400,
              mb: 2,
              mt: 0,
            }}
          >
            exibido no mobile quando não há produto da vez ativo. caso só um link seja
            preenchido ele que será exibido.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
            }}
          >
            <Box>
              {/* Preview da imagem placeholder */}
              <Box
                sx={{
                  mb: 1.5,
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: theme.palette.grey[700],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                }}
              >
                {placeholderImage && (
                  <Box
                    component="img"
                    src={placeholderImage}
                    alt="Preview placeholder"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Box>
              <CustomTextField
                value={placeholderImage}
                onChange={(e) => setPlaceholderImage(e.target.value)}
                label="link da imagem"
                fullWidth
              />
            </Box>
            <Box>
              {/* Preview da imagem placeholder com código de vendedor */}
              <Box
                sx={{
                  mb: 1.5,
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: theme.palette.grey[700],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                }}
              >
                {placeholderImageWithSeller && (
                  <Box
                    component="img"
                    src={placeholderImageWithSeller}
                    alt="Preview placeholder com código de vendedor"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Box>
              <CustomTextField
                value={placeholderImageWithSeller}
                onChange={(e) => setPlaceholderImageWithSeller(e.target.value)}
                label="link da imagem com código de vendedor"
                fullWidth
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Tapumes;
